import { useCreate, useDataProvider, useGetIdentity, useNotify } from "ra-core";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import type { InputProps } from "ra-core";
import { useIsMobile } from "@/hooks/use-mobile";
import type { Popover as PopoverPrimitive } from "@base-ui/react/popover";

export const AutocompleteCompanyInput = ({
  validate,
  label,
  modal,
}: Pick<InputProps, "validate" | "label"> &
  Pick<PopoverPrimitive.Root.Props, "modal">) => {
  const [create] = useCreate();
  const dataProvider = useDataProvider();
  const { identity } = useGetIdentity();
  const notify = useNotify();

  const handleCreateCompany = async (name?: string) => {
    if (!name) return;
    const cleanName = name.trim();
    try {
      // Check if company with this name (case-insensitive) already exists in CRM
      const existing = await dataProvider.getList("companies", {
        filter: { "name@ilike": cleanName },
        pagination: { page: 1, perPage: 10 },
        sort: { field: "id", order: "ASC" },
      });

      const matched = existing.data.find(
        (c) => c.name.trim().toLowerCase() === cleanName.toLowerCase(),
      );

      if (matched) {
        notify("resources.companies.autocomplete.found_existing", {
          type: "info",
          messageArgs: {
            _: `Linked to existing company "${matched.name}" to prevent duplicate`,
          },
        });
        return matched;
      }

      const newCompany = await create(
        "companies",
        {
          data: {
            name: cleanName,
            sales_id: identity?.id,
            created_at: new Date().toISOString(),
          },
        },
        { returnPromise: true },
      );
      return newCompany;
    } catch {
      notify("resources.companies.autocomplete.create_error", {
        type: "error",
        messageArgs: {
          _: "An error occurred while creating the company",
        },
      });
    }
  };
  const isMobile = useIsMobile();

  return (
    <AutocompleteInput
      label={label}
      optionText="name"
      helperText={false}
      onCreate={handleCreateCompany}
      createItemLabel="resources.companies.autocomplete.create_item"
      createLabel="resources.companies.autocomplete.create_label"
      validate={validate}
      modal={modal ?? isMobile}
    />
  );
};
